import React, { Suspense, useState } from 'react';

import { displayAxiosError } from '@apis/next/errors';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FormError } from '@utils/errors';
import { ZodiosError } from '@zodios/core';
import { AxiosError } from 'axios';
import { ZodError } from 'zod';

export interface ApiErrorDialogProps {
  errors: any[];
  onClose: () => void;
}

const LazyReporter = React.lazy(() => import('@monoid-dev/zod-monaco-reporter'));

export function ApiErrorDialog(props: ApiErrorDialogProps) {
  const { errors, onClose } = props;

  const [currentIndex, setCurrentIndex] = useState(0);

  const [ignored, setIgnored] = useState(false);

  const currentError = errors[Math.max(currentIndex, errors.length - 1)];

  const renderTable = (children: React.ReactNode) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        columnGap: '1rem',
        rowGap: '0.25rem',
        whiteSpace: 'pre',
        fontFamily: 'monospace',
        maxWidth: '100%',
      }}
    >
      {children}
    </div>
  );

  const renderError = (e: any) => {
    if (e instanceof ZodiosError && e.cause instanceof ZodError) {
      return (
        <>
          <h2>ZodiosError caused by ZodError</h2>

          {renderTable(
            <>
              <div>API</div>
              <div>
                {e.config?.method} {e.config?.url}
              </div>
              <div>Queries</div>
              <div>{JSON.stringify(e.config?.queries)}</div>
              <div>Params</div>
              <div>{JSON.stringify(e.config?.params)}</div>
              <div>Data</div>
              <div style={{ height: 550, marginTop: '0.25rem' }}>
                <Suspense fallback={<>Loading reporter...</>}>
                  <LazyReporter error={e.cause} value={e.data} />
                </Suspense>
              </div>
            </>,
          )}
        </>
      );
    } else if (e instanceof AxiosError) {
      return (
        <>
          <h2>AxiosError</h2>

          {renderTable(
            <>
              <div>API</div>
              <div>
                {e.config?.method} {e.config?.url}
              </div>
              <div>Status</div>
              <div>{e.response?.status}</div>
            </>,
          )}
        </>
      );
    } else {
      return (
        <>
          <h2>Error</h2>
          <div>
            {displayAxiosError(e) ??
              (e instanceof FormError
                ? e.getDisplayMessage() || 'Unknown Error'
                : (e as any)?.message)}
          </div>
        </>
      );
    }
  };

  return (
    <ActionableDialog
      open={!ignored && errors.length > 0}
      onClose={() => {
        onClose();
        setCurrentIndex(0);
      }}
      size="lg"
    >
      <ActionableDialogTitle>
        API Errors ({currentIndex + 1}/{errors.length})
      </ActionableDialogTitle>
      <ActionableDialogContent>
        <div style={{ padding: '1rem' }}>{renderError(currentError)}</div>
      </ActionableDialogContent>
      <ActionableDialogActions>
        <Button
          text="Ignore for Session"
          color="accent"
          size="small"
          onClick={() => {
            setIgnored(true);
          }}
        />

        <Button
          text="Ignore"
          color="accent"
          size="small"
          onClick={() => {
            localStorage.setItem('INGORE_API_ERROR_DIALOG', '1');
            alert('To re-enable API Error Dialog, drop INGORE_API_ERROR_DIALOG in localStorage. ');
          }}
        />

        <Button
          text="Store as $temp1"
          color="primary"
          size="small"
          onClick={() => {
            (globalThis as any).$temp1 = currentError;
            console.info('Visit error using $temp1');
          }}
        />

        <Button
          text="Log to Console"
          color="primary"
          size="small"
          onClick={() => console.info(currentError)}
        />

        <div style={{ flex: 1 }} />

        <Button
          text="Prev"
          color="primary"
          size="small"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((s) => s - 1)}
        />

        <Button
          text="Next"
          color="primary"
          size="small"
          disabled={currentIndex === errors.length - 1}
          onClick={() => setCurrentIndex((s) => s + 1)}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );
}
